import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Footer from '../../components/layout/Footer';
import '../../styles/design-system/index.css';

const AiPptGuidePage = () => {
  const navigate = useNavigate();

  const faqData = [
    { q: 'AI PPT 만들기, 정말 3분이면 되나요?', a: '네, 왓퀴즈에서 주제를 입력하면 AI가 아웃라인을 생성하고, 테마를 선택하면 PPTX 파일이 완성돼요. 실제 평균 소요 시간은 3분 이내예요.' },
    { q: 'AI가 만든 PPT의 품질은 어떤가요?', a: '교과 내용에 맞는 구조화된 슬라이드가 생성돼요. 디자인 전문가가 제작한 10가지 테마가 적용되어, 디자인 감각 없이도 깔끔한 PPT를 얻을 수 있어요.' },
    { q: '어떤 과목의 PPT를 만들 수 있나요?', a: '국어, 수학, 영어, 과학, 사회, 도덕, 체육, 음악, 미술 등 모든 교과의 수업 PPT를 만들 수 있어요. 교과서 PDF를 첨부하면 더 정확해요.' },
    { q: 'AI PPT를 수업 외에도 활용할 수 있나요?', a: '네, 학부모 설명회, 교직원 회의, 연수 발표, 수업 연구 등 교사의 다양한 발표 상황에 활용할 수 있어요.' },
    { q: 'ChatGPT로 PPT를 만드는 것과 뭐가 다른가요?', a: 'ChatGPT는 텍스트만 생성하므로 직접 PPT에 붙여넣어야 해요. 왓퀴즈는 텍스트 생성부터 슬라이드 디자인, PPTX 파일 다운로드까지 한 번에 완성돼요.' },
  ];

  return (
    <>
      <Helmet>
        <title>AI PPT 만들기 완벽 가이드 | 교사 수업 PPT 3분 만에 만드는 법 (2026)</title>
        <meta name="description" content="AI PPT 만들기 방법을 단계별로 안내해요. 교사가 수업 PPT를 3분 만에 만드는 실전 가이드. AI PPT 도구 비교, 프롬프트 팁, 과목별 예시 포함. 왓퀴즈 활용법." />
        <meta name="keywords" content="AI PPT 만들기, AI PPT 만들기 방법, 교사 PPT 만들기, 수업 PPT 만들기, AI 프레젠테이션 만들기, PPT 자동 생성, 교사용 AI PPT, 수업자료 PPT, AI PPT 도구, PPT 만들기 가이드" />
        <link rel="canonical" href="https://whatquiz.co.kr/guide/ai-ppt" />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://whatquiz.co.kr/guide/ai-ppt" />
        <meta property="og:title" content="AI PPT 만들기 완벽 가이드 | 교사 수업 PPT 3분 만에 만드는 법" />
        <meta property="og:description" content="AI PPT 만들기 방법을 단계별로 안내. 프롬프트 팁, 과목별 예시, AI 도구 비교 포함." />
        <meta property="og:image" content="https://whatquiz.co.kr/og-image-whatquiz-2025.png" />
        <meta property="og:locale" content="ko_KR" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AI PPT 만들기 완벽 가이드 | 교사 수업 PPT 3분 만에 만드는 법" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Article",
                "headline": "AI PPT 만들기 완벽 가이드",
                "description": "교사가 AI로 수업 PPT를 3분 만에 만드는 실전 가이드",
                "author": { "@type": "Organization", "name": "왓퀴즈" },
                "publisher": { "@type": "Organization", "name": "왓퀴즈", "logo": "https://whatquiz.co.kr/images/logo512.png" },
                "datePublished": "2026-03-09",
                "dateModified": "2026-03-09"
              },
              {
                "@type": "FAQPage",
                "mainEntity": faqData.map(f => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } }))
              }
            ]
          })}
        </script>
      </Helmet>

      <div style={{ fontFamily: 'var(--whatquiz-font-family)', backgroundColor: '#fff', minHeight: '100vh' }}>
        {/* 히어로 */}
        <section style={{ padding: '80px 20px 60px', textAlign: 'center', background: 'linear-gradient(180deg, #eff6ff 0%, #fff 100%)' }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <span style={{ display: 'inline-block', padding: '6px 16px', backgroundColor: '#dbeafe', color: '#1d4ed8', borderRadius: '20px', fontSize: '14px', fontWeight: '600', marginBottom: '16px' }}>
              2026년 최신 가이드
            </span>
            <h1 style={{ fontSize: 'clamp(28px, 5vw, 42px)', fontWeight: '800', color: '#111', lineHeight: '1.3', marginBottom: '20px' }}>
              AI PPT 만들기 완벽 가이드
            </h1>
            <p style={{ fontSize: '18px', color: '#64748b', lineHeight: '1.7', marginBottom: '32px' }}>
              AI PPT 만들기란 AI가 주제와 교과 내용을 분석해 수업용 프레젠테이션을 자동으로 생성하는 기술이에요.
              이 가이드에서는 교사가 AI로 수업 PPT를 3분 만에 만드는 실전 방법을 단계별로 안내해요.
            </p>
            <button onClick={() => navigate('/ai-ppt')} style={{ padding: '14px 28px', background: '#3182f6', color: '#fff', fontSize: '16px', fontWeight: '700', border: 'none', borderRadius: '12px', cursor: 'pointer' }}>
              지금 바로 PPT 만들기 →
            </button>
          </div>
        </section>

        {/* 본문 */}
        <article style={{ maxWidth: '720px', margin: '0 auto', padding: '60px 20px' }}>
          <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#111', marginBottom: '16px' }}>AI로 PPT를 어떻게 만드나요?</h2>
          <p style={{ fontSize: '16px', color: '#334155', lineHeight: '1.8', marginBottom: '32px' }}>
            왓퀴즈에서 AI PPT를 만드는 과정은 간단해요. 주제를 입력하고, AI가 생성한 아웃라인을 확인한 뒤, 테마를 선택하면 PPTX 파일이 다운로드돼요.
          </p>

          <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#111', marginBottom: '12px' }}>1단계: 주제 입력</h3>
          <p style={{ fontSize: '16px', color: '#334155', lineHeight: '1.8', marginBottom: '24px' }}>
            왓퀴즈 채팅에서 "PPT 만들기" 카테고리를 선택하고, 수업 주제를 자연어로 입력해요.
            예를 들어 "중학교 2학년 과학, 광합성 단원 수업 PPT"라고 입력하면 돼요.
            교과서 PDF나 한글 파일을 첨부하면 해당 내용을 기반으로 더 정확한 PPT가 생성돼요.
          </p>

          <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#111', marginBottom: '12px' }}>2단계: 아웃라인 확인 및 수정</h3>
          <p style={{ fontSize: '16px', color: '#334155', lineHeight: '1.8', marginBottom: '24px' }}>
            AI가 생성한 슬라이드 아웃라인이 미리보기로 제공돼요.
            슬라이드 순서를 변경하거나, 내용을 추가/삭제할 수 있어요.
            아웃라인이 마음에 들면 다음 단계로 진행해요.
          </p>

          <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#111', marginBottom: '12px' }}>3단계: 테마 선택 후 다운로드</h3>
          <p style={{ fontSize: '16px', color: '#334155', lineHeight: '1.8', marginBottom: '32px' }}>
            10가지 수업용 테마 중 원하는 디자인을 선택해요.
            깔끔한 교실 수업용부터 학부모 설명회용까지 다양한 테마가 있어요.
            테마를 선택하면 PPTX 파일이 생성되어 다운로드돼요.
          </p>

          <div style={{ background: '#f0f7ff', borderRadius: '16px', padding: '24px', marginBottom: '40px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1d4ed8', marginBottom: '12px' }}>프롬프트 작성 팁</h3>
            <ul style={{ fontSize: '15px', color: '#334155', lineHeight: '2', paddingLeft: '20px' }}>
              <li><strong>학년/과목을 명시</strong>하세요 (예: "초등 5학년 사회")</li>
              <li><strong>단원이나 주제를 구체적으로</strong> 적으세요 (예: "민주주의의 발전 과정")</li>
              <li><strong>슬라이드 수를 지정</strong>하면 좋아요 (예: "10장 내외로")</li>
              <li><strong>교과서 PDF를 첨부</strong>하면 내용 정확도가 높아져요</li>
            </ul>
          </div>

          <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#111', marginBottom: '16px', marginTop: '48px' }}>AI PPT 도구 비교</h2>
          <p style={{ fontSize: '16px', color: '#334155', lineHeight: '1.8', marginBottom: '24px' }}>
            교사가 사용할 수 있는 AI PPT 도구를 비교해볼게요.
          </p>

          <div style={{ overflowX: 'auto', marginBottom: '40px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ background: '#f1f5f9' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '700' }}>도구</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '700' }}>교과 반영</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '700' }}>파일 출력</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '700' }}>추가 기능</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['왓퀴즈', '교과서 기반 AI 분석', 'PPTX', '생기부+학습지+채점'],
                  ['미리캔버스', '템플릿 기반', 'PPTX/PDF', '디자인 템플릿'],
                  ['Gamma', '범용 AI', '웹 링크', '웹 발표'],
                  ['ChatGPT', '프롬프트 의존', '텍스트만', '범용 대화'],
                  ['MYAIEDU', '교육과정 기반', '편집기', '활동지 생성'],
                ].map(([name, curriculum, output, extra], idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0', background: idx === 0 ? '#f0f7ff' : 'transparent' }}>
                    <td style={{ padding: '12px 16px', fontWeight: idx === 0 ? '700' : '400', color: idx === 0 ? '#1d4ed8' : '#334155' }}>{name}</td>
                    <td style={{ padding: '12px 16px' }}>{curriculum}</td>
                    <td style={{ padding: '12px 16px' }}>{output}</td>
                    <td style={{ padding: '12px 16px' }}>{extra}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* FAQ */}
          <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#111', marginBottom: '24px', marginTop: '48px' }}>자주 묻는 질문</h2>
          {faqData.map((faq, idx) => (
            <div key={idx} style={{ borderBottom: '1px solid #e2e8f0', padding: '20px 0' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#111', marginBottom: '8px' }}>{faq.q}</h3>
              <p style={{ fontSize: '15px', color: '#64748b', lineHeight: '1.7' }}>{faq.a}</p>
            </div>
          ))}
        </article>

        {/* CTA */}
        <section style={{ padding: '60px 20px', textAlign: 'center', background: 'linear-gradient(180deg, #f0f7ff 0%, #fff 100%)' }}>
          <h2 style={{ fontSize: '24px', fontWeight: '800', color: '#111', marginBottom: '12px' }}>수업 PPT, AI로 3분 만에 만들어보세요</h2>
          <p style={{ fontSize: '16px', color: '#64748b', marginBottom: '24px' }}>교사 인증 후 무료로 시작할 수 있어요</p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/ai-ppt')} style={{ padding: '14px 28px', background: '#3182f6', color: '#fff', fontSize: '16px', fontWeight: '700', border: 'none', borderRadius: '12px', cursor: 'pointer' }}>
              AI PPT 만들기
            </button>
            <button onClick={() => navigate('/guide/ai-worksheet')} style={{ padding: '14px 28px', background: '#fff', color: '#334155', fontSize: '16px', fontWeight: '600', border: '1px solid #e2e8f0', borderRadius: '12px', cursor: 'pointer' }}>
              학습지 가이드 보기
            </button>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default AiPptGuidePage;
